import React from "react";
import { Box, Button, Chip, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { formatDate } from "../../common/constant/dates";
import { formatCount } from "../../common/utils/format";
import {
  AdminUserRow,
  deleteUserAccount,
  getUserCounts,
  getUsersAccessTable,
  updateSellerStatus,
} from "../../common/requests/users/endpoints";
import { revokeAdminAccess } from "../../common/requests/admins/endpoints";
import { PilotSeller, getPilotSellers } from "../../common/requests/bringBuyers/endpoints";
import { formatMoney } from "../../common/utils/format";
import {
  Banner,
  Column,
  ConfirmDialog,
  DataTable,
  KpiCard,
  KpiStrip,
  StatusPill,
  errorMessage,
  useResource,
  useToast,
  useUrlParam,
} from "../../ui";
import UserDialog from "./UserDialog";
import AdminAccessDialog from "./AdminAccessDialog";
import SellerSettingsDialog from "./dialogs/SellerSettingsDialog";
import { filterPilot } from "./bringBuyersData";
import { UserSegment, filterUsers, findUser, roleLabel, roleTone, rowId, sameId, tariffPercentText, userKind } from "./userData";

const PAGE_SIZE = 50;

type Confirm = { kind: "revoke" | "delete" | "disableSeller"; user: AdminUserRow } | null;

/** The Users tab of Users & Sellers: counts, the searchable user list and one
 * user dialog (`?user=<id>`) holding every action. The list and actions are
 * superadmin only; others with registered_users see the counts. */
export default function UserManagement() {
  const { idToken, adminUser: currentUser, hasPermission } = useAuth();
  const toast = useToast();
  const isSuper = currentUser?.role === "superadmin";

  const counts = useResource(async () => (await getUserCounts(idToken!)).data, [idToken], {
    enabled: !!idToken && hasPermission("registered_users"),
    errorText: "Couldn't load user counts.",
  });
  const users = useResource(async () => (await getUsersAccessTable(idToken!)).data, [idToken], {
    enabled: !!idToken && isSuper,
    errorText: "Couldn't load users.",
  });

  // Bring Your Buyers pilot sellers, for the filter chip next to search.
  const pilot = useResource(async () => (await getPilotSellers(idToken!)).data, [idToken], {
    enabled: !!idToken && isSuper,
    errorText: "Couldn't load Bring Your Buyers sellers.",
  });
  const [pilotOnly, setPilotOnly] = React.useState(false);

  const [segment, setSegment] = React.useState<UserSegment>("total");
  const [query, setQuery] = React.useState("");
  const [shown, setShown] = React.useState(PAGE_SIZE);
  const [openId, setOpenId] = useUrlParam("user");

  const all = React.useMemo(() => users.data ?? [], [users.data]);
  const rows = React.useMemo(() => filterUsers(all, segment, query), [all, segment, query]);
  const pilotRows = React.useMemo(() => filterPilot(pilot.data?.sellers ?? [], query), [pilot.data, query]);
  const adminCount = React.useMemo(() => all.filter((u) => u.admin_user_id).length, [all]);
  const openUser = findUser(all, openId);

  // Dialog state
  const [accessOpen, setAccessOpen] = React.useState(false);
  const [sellerEdit, setSellerEdit] = React.useState<"tariff" | "whatsapp" | null>(null);
  const [confirm, setConfirm] = React.useState<Confirm>(null);
  const [confirmBusy, setConfirmBusy] = React.useState(false);
  const [confirmError, setConfirmError] = React.useState<string | null>(null);
  const [sellerBusy, setSellerBusy] = React.useState(false);

  const isSelf = (u: AdminUserRow | null) => !!u && sameId(u.user_id, currentUser?.admin_user_id);

  const patchUser = (userId: string, updates: Partial<AdminUserRow>) =>
    users.setData((prev) => (prev ? prev.map((row) => (sameId(row.user_id, userId) ? { ...row, ...updates } : row)) : prev));

  const setSeller = async (user: AdminUserRow, enable: boolean) => {
    if (!idToken || !user.user_id) return;
    setSellerBusy(true);
    try {
      const { data } = await updateSellerStatus(user.user_id, enable, idToken);
      patchUser(user.user_id, { is_seller: data.is_seller, tariff_rate: data.tariff_rate ?? user.tariff_rate });
      toast(enable ? "Seller turned on" : "Seller turned off; their listings are hidden");
      counts.reload();
      return true;
    } catch (err) {
      toast(errorMessage(err, "Couldn't update seller status."), "error");
      return false;
    } finally {
      setSellerBusy(false);
    }
  };

  const runConfirm = async () => {
    if (!confirm || !idToken) return;
    const { kind, user } = confirm;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      if (kind === "disableSeller") {
        setConfirmBusy(false);
        setConfirm(null);
        await setSeller(user, false);
        return;
      }
      if (kind === "revoke" && user.admin_user_id) {
        await revokeAdminAccess(user.admin_user_id, idToken);
        toast("Admin access removed. Their marketplace account is unchanged.");
      } else if (kind === "delete" && user.user_id) {
        await deleteUserAccount(user.user_id, idToken);
        toast("Buyer account deleted");
        setOpenId(null);
      }
      setConfirm(null);
      await users.reload();
      counts.reload();
    } catch (err) {
      setConfirmError(errorMessage(err, kind === "revoke" ? "Couldn't remove admin access." : "Couldn't delete this account."));
    } finally {
      setConfirmBusy(false);
    }
  };

  const columns: Column<AdminUserRow>[] = [
    {
      key: "user",
      header: "User",
      card: "title",
      render: (u) => (
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{u.display_name || "–"}</Box>
          <Box sx={{ fontSize: 12.5, fontWeight: 400, color: C.muted, overflowWrap: "anywhere" }}>{u.email || "–"}</Box>
        </Box>
      ),
    },
    {
      key: "kind",
      header: "Type",
      card: "aside",
      render: (u) => {
        const k = userKind(u);
        return (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: { xs: "flex-end", md: "flex-start" } }}>
            <StatusPill tone={k.tone}>{k.label}</StatusPill>
            {u.role && <StatusPill tone={roleTone(u.role)}>{roleLabel(u.role)}</StatusPill>}
          </Box>
        );
      },
    },
    {
      key: "tariff",
      header: "Fee",
      hideBelow: 900,
      render: (u) => (userKind(u).label !== "Buyer" ? tariffPercentText(u.tariff_rate) : <Box component="span" sx={{ color: C.faint }}>–</Box>),
    },
    {
      key: "whatsapp",
      header: "WhatsApp",
      hideBelow: 1100,
      card: "hidden",
      render: (u) =>
        u.is_seller ? (
          u.whatsapp_number || <Box component="span" sx={{ color: C.muted }}>Not set</Box>
        ) : (
          <Box component="span" sx={{ color: C.faint }}>–</Box>
        ),
    },
    { key: "joined", header: "Joined", hideBelow: 1000, render: (u) => formatDate(u.created_at) },
  ];

  const pilotColumns: Column<PilotSeller>[] = [
    {
      key: "seller",
      header: "Seller",
      card: "title",
      render: (p) => (
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
            {p.username || p.email || p.seller_id}
            {!p.is_seller && (
              <Box component="span" sx={{ ml: 0.75, verticalAlign: "middle" }}>
                <StatusPill tone="danger">Seller off</StatusPill>
              </Box>
            )}
          </Box>
          <Box sx={{ fontSize: 12.5, fontWeight: 400, color: C.muted, overflowWrap: "anywhere" }}>{p.email || "–"}</Box>
        </Box>
      ),
    },
    {
      key: "code",
      header: "Code",
      card: "subtitle",
      render: (p) =>
        p.code ? (
          <Box component="span" className="mono" sx={{ fontWeight: 700 }}>
            {p.code}
          </Box>
        ) : (
          <Box component="span" sx={{ color: C.muted }}>
            No code
          </Box>
        ),
    },
    { key: "buyers", header: "Buyers", align: "right", card: "field", render: (p) => (p.stats ? formatCount(p.stats.buyers) : "–") },
    { key: "orders", header: "Orders at 0%", align: "right", card: "field", render: (p) => (p.stats ? formatCount(p.stats.orders) : "–") },
    {
      key: "saved",
      header: "Commission saved",
      align: "right",
      card: "aside",
      render: (p) => (p.stats ? formatMoney(p.stats.fees_saved, p.stats.currency || "SGD") : "–"),
    },
    {
      key: "vouchers",
      header: "Vouchers paid",
      align: "right",
      hideBelow: 900,
      card: "field",
      render: (p) => (p.stats && p.stats.vouchers ? formatMoney(p.stats.vouchers_paid, p.stats.currency || "SGD") : "–"),
    },
    { key: "since", header: "On since", hideBelow: 1100, card: "hidden", render: (p) => (p.enabled_at ? formatDate(p.enabled_at) : "–") },
  ];

  const segmentCard = (key: UserSegment, title: string, value: number | null | undefined) => (
    <KpiCard
      title={title}
      value={value === null || value === undefined ? "–" : formatCount(value)}
      active={isSuper && !pilotOnly && segment === key}
      onClick={
        isSuper
          ? () => {
              setSegment(key);
              setPilotOnly(false);
              setShown(PAGE_SIZE);
            }
          : undefined
      }
    />
  );

  return (
    <>
      {counts.status === "error" && !counts.data ? (
        <Banner tone="danger" title="Counts unavailable." action={<Button onClick={counts.reload}>Try again</Button>}>
          {counts.error}
        </Banner>
      ) : (
        <KpiStrip max={isSuper ? 4 : 3}>
          {segmentCard("total", "Total users", counts.data?.total)}
          {segmentCard("sellers", "Sellers", counts.data?.sellers)}
          {segmentCard("buyers", "Buyers", counts.data?.buyers)}
          {isSuper && segmentCard("admins", "Admins", users.data ? adminCount : null)}
        </KpiStrip>
      )}

      {!isSuper ? (
        <Banner title="Superadmins only.">You can see user counts. Managing users and access is restricted to superadmins.</Banner>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              placeholder={pilotOnly ? "Search name, email or code" : "Search name, email or user ID"}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShown(PAGE_SIZE);
              }}
              inputProps={{ "aria-label": "Search users" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: C.paper, flex: "1 1 260px", minWidth: 0 }}
            />
            {pilot.data?.available && (
              <Chip
                label={`Bring Your Buyers · ${formatCount(pilot.data.sellers.length)}`}
                color={pilotOnly ? "primary" : "default"}
                variant={pilotOnly ? "filled" : "outlined"}
                onClick={() => setPilotOnly((on) => !on)}
                aria-pressed={pilotOnly}
                sx={{ height: 44, fontWeight: 600 }}
              />
            )}
          </Box>
          {pilotOnly ? (
            <DataTable
              aria-label="Bring Your Buyers sellers"
              rows={pilotRows}
              columns={pilotColumns}
              getRowId={(p) => p.seller_id}
              loading={pilot.status === "loading" || pilot.status === "idle"}
              error={pilot.error}
              onRetry={pilot.reload}
              empty={query.trim() ? "No pilot sellers match your search." : "No sellers in Bring Your Buyers yet. Turn it on from a seller's dialog."}
              onRowClick={(p) => setOpenId(p.seller_id, true)}
              selectedId={openUser ? rowId(openUser) : null}
            />
          ) : (
            <DataTable
              aria-label="Users"
              rows={rows.slice(0, shown)}
              columns={columns}
              getRowId={rowId}
              loading={users.status === "loading" || users.status === "idle"}
              error={users.error}
              onRetry={users.reload}
              empty={query.trim() ? "No users match your search." : "No users in this group."}
              onRowClick={(u) => u.user_id && setOpenId(u.user_id.trim(), true)}
              selectedId={openUser ? rowId(openUser) : null}
              rowTone={(u) => (userKind(u).tone === "danger" ? "danger" : undefined)}
              footer={
                rows.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", px: { xs: 0, md: 2 }, py: 1, fontSize: 13, color: C.muted }}>
                    <span>
                      Showing {formatCount(Math.min(shown, rows.length))} of {formatCount(rows.length)}
                    </span>
                    {shown < rows.length && (
                      <Button variant="outlined" onClick={() => setShown((n) => n + PAGE_SIZE)} sx={{ minHeight: 44 }}>
                        Show {Math.min(PAGE_SIZE, rows.length - shown)} more
                      </Button>
                    )}
                  </Box>
                )
              }
            />
          )}

          <UserDialog
            open={!!openId}
            loading={!users.data && users.status !== "error"}
            user={openUser}
            requestedId={openId}
            isSelf={isSelf(openUser)}
            sellerBusy={sellerBusy}
            onClose={() => setOpenId(null)}
            onEditSeller={(focus) => setSellerEdit(focus)}
            onSetSeller={(enable) => {
              if (!openUser) return;
              // Turning a seller off hides all their live listings, so confirm first.
              if (enable) setSeller(openUser, true);
              else {
                setConfirmError(null);
                setConfirm({ kind: "disableSeller", user: openUser });
              }
            }}
            onBringBuyersChanged={pilot.reload}
            onEditAccess={() => setAccessOpen(true)}
            onRevokeAccess={() => {
              if (!openUser) return;
              setConfirmError(null);
              setConfirm({ kind: "revoke", user: openUser });
            }}
            onDelete={() => {
              if (!openUser) return;
              setConfirmError(null);
              setConfirm({ kind: "delete", user: openUser });
            }}
          />

          <AdminAccessDialog open={accessOpen && !!openUser} user={openUser} onClose={() => setAccessOpen(false)} onSaved={users.reload} />
          <SellerSettingsDialog
            open={!!sellerEdit && !!openUser}
            user={openUser}
            focus={sellerEdit ?? "tariff"}
            onClose={() => setSellerEdit(null)}
            onSaved={patchUser}
            onReload={users.reload}
          />
          <ConfirmDialog
            open={!!confirm}
            tone="danger"
            busy={confirmBusy}
            error={confirmError}
            onClose={() => setConfirm(null)}
            onConfirm={runConfirm}
            title={confirm?.kind === "revoke" ? "Remove admin access?" : confirm?.kind === "delete" ? "Delete buyer account?" : "Turn off seller?"}
            confirmLabel={confirm?.kind === "revoke" ? "Remove access" : confirm?.kind === "delete" ? "Delete account" : "Turn off seller"}
            body={
              confirm && (
                <Box sx={{ fontSize: 14 }}>
                  <b>{confirm.user.email || confirm.user.display_name}</b>
                  {confirm.kind === "revoke"
                    ? " will no longer be able to sign in to this dashboard. Their marketplace account, orders and listings are not affected."
                    : confirm.kind === "delete"
                      ? " will be permanently deleted. This can't be undone. Accounts with orders or other records can't be deleted."
                      : " becomes a buyer and all of their live listings are deactivated. Turning seller back on restores those listings."}
                </Box>
              )
            }
          />
        </>
      )}
    </>
  );
}
